<template lang="pug">
.register-row
  .register-group
    .register-key
      .left-side.d-flex
        span.key-name {{ keyName }}
        span.comment(v-if="comment") {{ comment }}
        span.validation-label(v-if="required == true") 必須
      .right-side
        slot(name="question")

    .register-value
      MultiFileSelecter(
        :mimetypes="mimetypes"
        :width="width"
        :height="height"
        :limit="limit"
        :showPreview="showPreview"
        :enablePreviewStatus="enablePreviewStatus"
        @input="inputFiles"
        @preview="onPreview"
      )
    
    input(v-model="_value" :name="name" :data-vv-as="keyName" style="display: none")

    .error-messages.text-break(v-for="error in errors.collect(name)" :style="{ 'maxWidth': width }")
      label.error-message {{ error }}
      br

  slot(name="default")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";
import ImageFileCompression from "@/commons/utils/ImageFileCompression";
import MultiFileSelecter from "@/pc/basics/MultiFileSelecter.vue";

/**
 * 入力要素コンポーネント
 *
 * - ファイル選択をサポートする
 * - 既存の入力画面のスタイルが適用される
 * - バリデーターは親コンポーネントのものをInjectionして使用する
 *   - そのため、親コンポーネントでveevalidateをnewしておく必要がある
 */
@Component({
  components: {
    MultiFileSelecter
  }
})
export default class FormMultiFile extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;

  /** 親コンポーネントでv-modelで指定した値 */
  @Prop({ required: true })
  value!: Array<any>;

  /** 項目名 */
  @Prop({ required: true, type: String })
  keyName!: string;

  /** 必須か */
  @Prop({
    default: () => {
      return false;
    },
    type: Boolean
  })
  required!: boolean;

  /** 注釈 */
  @Prop({ type: String })
  comment!: string;

  /** name属性の値 */
  @Prop({ required: true, type: String })
  name!: string;

  /** veevalidateに渡すバリデーションルール */
  @Prop({ required: true })
  validationRules!: [];

  /** コンポーネント横幅 */
  @Prop({
    default: () => {
      return "600px";
    },
    type: String
  })
  width!: string;

  /** コンポーネント縦幅 */
  @Prop({
    default: () => {
      return "74px";
    },
    type: String
  })
  height!: string;

  /** ファイルのmimeType */
  @Prop({ required: true, type: Array })
  mimetypes!: Array<string>;

  /** 入力できる最大ファイル数 */
  @Prop({ required: true, type: Number })
  limit!: number;

  @Prop({
    default: () => {
      return true;
    },
    type: Boolean
  })
  showPreview!: boolean;

  /** プレビューボタンの状態(活性：true、非活性：false) */
  @Prop({ required: true, type: Boolean })
  enablePreviewStatus!: boolean;

  /** 子コンポーネントのv-modelでpropの値を指定してはいけないのでgetterにする */
  get _value(): Array<any> {
    return this.value;
  }

  /** 子コンポーネントでファイルが選択されたイベント処理 */
  inputFiles(files: Array<File>): void {
    this.inputValue(files);
    return;
  }

  /**
   * ### 選択されたファイルを親コンポーネントに返す
   *
   * - バリデーションして有効なファイルを選別する
   * - 有効なファイルが1件以上あれば親コンポーネントに通知する
   */
  public async inputValue(files: Array<File>): Promise<void> {
    // PDFは並び順として優先したいので、ソートする
    const sortedFiles: Array<File> = this.sortFiles(files.slice());

    // 画像はサイズが大きい場合は圧縮する
    let compressedFiles: Array<File> = [];
    for (let file of sortedFiles) {
      if (file.type == "image/jpeg") {
        const compressedFile: File | undefined = await ImageFileCompression.imageCompression(file, "image/jpeg", 1 * 1024 * 1024, 4096, 4096);
        compressedFiles.push(compressedFile ? compressedFile : file);
      } else {
        compressedFiles.push(file);
      }
    }

    const validFiles: Array<File> = await this.validation(compressedFiles);

    if (validFiles.length < 1) {
      return;
    }

    this.$emit("input", validFiles.slice(0, this.limit));

    return;
  }

  /**
   * ### バリデーション
   *
   * - 各ファイルのバリデーションを行い、エラーの場合は空の配列を返す
   * - バリデーションのkey名はファイル名とする
   * - 最大ファイル数以上の場合は空の配列を返す
   */
  public async validation(files: Array<File>): Promise<Array<File>> {
    let result: Array<File> = [];

    // 現在のバリデーションエラーをリセットしてから、バリデーションを実施する
    this.$validator.errors.remove(this.name);

    for (let file of files) {
      // mimetype毎のバリデーションルールでバリデーションを実施する
      // mimetypeが該当しない場合は0番目のルールでバリデーションする
      let type = this.mimetypes.indexOf(file.type);
      type = type == -1 ? 0 : type;

      const { valid, errors } = await this.$validator.verify(file, this.validationRules[type], { name: file.name, bails: false });

      if (!valid) {
        for (const error of errors) {
          this.$validator.errors.add({ field: this.name, msg: error });
          return [];
        }
      } else {
        result.push(file);
      }
    }

    // 登録数上限は自前でバリデーションする
    if (this.limit < result.length) {
      this.$validator.errors.add({ field: this.name, msg: `${this.keyName}最大登録ファイル数以上は登録できません` });
      return [];
    }

    return result;
  }

  /**
   * ### ファイルのソート
   *
   * - アプリ仕様により、pdfは優先的に前にする
   * - その他はファイル名でソートする
   */
  public sortFiles(files: Array<File>): Array<File> {
    const sortedFiles: Array<File> = files.sort((a: File, b: File) => {
      const nameA: string = a.name;
      const typeA: string = a.type;

      const nameB: string = b.name;
      const typeB: string = b.type;

      // aがpdfの場合、aが先
      if (typeA == "application/pdf" && typeB != "application/pdf") {
        return -1;
      }
      // bがpdfの場合、bが先
      if (typeA != "application/pdf" && typeB == "application/pdf") {
        return 1;
      }
      // a、bが両方pdfまたは両方pdfでない場合、名前順でソート
      if (nameA > nameB) {
        return 1;
      } else {
        return -1;
      }
    });

    return sortedFiles;
  }

  /**
   * ### 子コンポーネントでプレビューボタンのイベント処理
   *
   * 親コンポーネントにプレビューボタン押下を通知する
   */
  public onPreview(): void {
    this.$emit("preview");

    return;
  }
}
</script>
<style lang="scss" scoped>
.right-side {
  margin-left: auto;
}
</style>
